import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { useGlobalContext } from 'GlobalContext';
import { useSettings } from 'services/settings/SettingsContext';
import { useModal } from './ModalContext';
import { EditableStatusType, FieldOption, FieldOptionFetchResponse, FieldType } from 'types/FieldTypes';
import { fetchDataAndOptionsOfDetailCards } from 'services/api/fetchFieldData';
import { TabType } from 'types/TabTypes';
import { DetailCardType } from 'types/DetailPageTypes';
import { TabBar } from 'components/layout/TabBar';
import StatusBar from 'components/layout/StatusBar';
import FieldsCard from 'components/detailcards/FieldsCard';
import GenerateHeaderTitle from './functions/GenerateHeaderTitle';
import PhotosCard from 'components/detailcards/PhotosCard';
import TabsCard from 'components/detailcards/TabsCard';
import MessagesCard from 'components/detailcards/MessagesCard';
import { HeaderButtons, HeaderButtonsConfig } from 'types/ButtonTypes';
import generateHeaderButtons from './functions/generateHeaderButtons';
import DetailModalSummary from './DetailModalSummary';
import { isArrayOfConditions } from 'services/utils/isArrayOfConditions';
import { useFetchContext } from 'services/api/FetchContext';
import SignatureSection from 'components/detailcards/SignaturesSection';
import InvoiceScheduleCard from '../../views/invoices/InvoiceScheduleCard';
import '../../style/scss/modals.scss';

export interface DetailModalProps {
    viewKey?: string;
    objectType?: 'proposal' | 'order' | 'purchase' | 'job' | 'project' | 'invoice' | 'invoice_template';
    apiObject?: string;
    itemId?: number;
    headerButtons?: HeaderButtonsConfig;
    statusBar?: boolean;
    sections?: DetailCardType[];
    tabs?: TabType[];
    summary?: boolean;
    extraProps?: Record<string, any>;
    toggleSideTab?: (showSideTab: boolean) => void;
    refetchList?: () => void;
}

const DetailModal: React.FC<DetailModalProps> = ({ 
    viewKey, objectType, apiObject, itemId, headerButtons, statusBar, sections, tabs, summary = false, extraProps, refetchList
}) => {
    const { t } = useTranslation();
    const { setFloatingAlert } = useGlobalContext();
    const { userHash, activeFeatures, roleFeatures, allowedFeatures, allowedRights, handleLogout } = useAuthContext();
    const { userLocale, userTimezone } = useSettings();
    const { getCache, setCache } = useFetchContext();
    const { initializeModal, revealModal, closeModal } = useModal();
    const [data, setData] = useState(null);
    const [statuses, setStatuses] = useState(null);
    const [currentStatus, setCurrentStatus] = useState<number | null>(null);
    const [loadStatus, setLoadStatus] = useState<"idle" | "loading" | "loaded">("idle");
    const [dropdownData, setDropdownData] = useState<Record<string, FieldOptionFetchResponse>>({});
    const [groupOptions, setGroupOptions] = useState<FieldOption[]>([])
    const [buttonLoader, setButtonLoader] = useState<Record<string, boolean>>({});
    const [editable, setEditable] = useState<EditableStatusType>({ editability: true });

    // Fetch the data on initial render
    useEffect(() => {
        fetchData();
    }, []);

    // Function to fetch the data to be used on initial render and for refetches
    const fetchData = async () => {
        if (!itemId || !apiObject) return;
        
        // Fetch the data
        const fetchedData = await fetchDataAndOptionsOfDetailCards(
            apiObject, 
            itemId.toString(),
            dropdownData, 
            groupOptions,
            headerButtons, 
            sections, 
            activeFeatures,
            setData, 
            setDropdownData, 
            setGroupOptions, 
            setLoadStatus, 
            getCache,
            setCache,
            handleLogout
        )

        if (!fetchedData) return;

        // Get the fetched data of the object
        const [fetchedObjectData] = fetchedData;

        // Derive the editability from the fetched data on every fetch, so a
        // reopened or restored object becomes editable again without reopening the modal
        let newEditable: EditableStatusType = { editability: true };

        // Set editability to false if the object is deleted
        if (fetchedObjectData['deleted'] === true) {
            newEditable = { editability: false };
        }

        // Set editability of the modal based on the data
        if (objectType) {

            // For projects which are closed
            if (objectType === 'project' && fetchedObjectData['closed'] === true) {
                newEditable = { editability: false };
            }

            // For jobs which have the finished resolution
            if (objectType === 'job' && fetchedObjectData['resolution'] === 'finished') {
                newEditable = { editability: false };
            }

            // For invoices which are not drafts
            if (objectType === 'invoice' && fetchedObjectData['status'] != 'draft') {
                newEditable = { 
                    editability: false, 
                    warning: {
                        header: 'invoice.general.edit_warning_header',
                        message: 'invoice.general.edit_warning_message',
                        buttonLabel: 'invoice.general.edit_warning_button_label' 
                    }
                };
            }

            // For proposals which are not drafts
            if (objectType === 'proposal' && fetchedObjectData['status'] != 'draft') {
                newEditable = { 
                    editability: false, 
                    warning: {
                        header: 'proposal.general.edit_warning_header',
                        message: 'proposal.general.edit_warning_message',
                        buttonLabel: 'proposal.general.edit_warning_button_label' 
                    }
                };
            }
        }

        // Only update the editability when it changed, to avoid rerendering the cards on every refetch
        setEditable(previous => JSON.stringify(previous) === JSON.stringify(newEditable) ? previous : newEditable);

        // Set the statuses from the received data
        if (statusBar) {
            setStatuses(fetchedObjectData['workflow']['statuses']);
            setCurrentStatus(fetchedObjectData['status']);
        }

        // Return the fetched data
        return fetchedData;
    }
    
    useEffect(() => {
        if (data !== null) {
            // Reveal the modal after successfully fetching the data
            revealModal();
        }
    }, [data])

    // Initialize the tab bar if there are tabs given
    let tabLabel, tabComponent;
    if (tabs && itemId && viewKey) {
        ({ tabLabel, tabComponent } = TabBar({
            tabs, 
            data, 
            viewKey, 
            itemId: itemId.toString(), 
            pageContext: apiObject,
            editable: editable,
            extraProps: extraProps
        }));
    }
    
    // Determine if a card should be hidden because it has no content while its hide when empty condition matches
    const shouldHideEmptyCard = (section: DetailCardType): boolean => {

        // Only check when the hide when empty config and the data are present
        if (!section.hideWhenEmpty || !data) return false;

        // Only hide the card when the data matches the configured condition
        const conditionMet = section.hideWhenEmpty.dataCondition.every(condition =>
            condition.values.includes(data[condition.field])
        );
        if (!conditionMet) return false;

        // Hide the card when every configured data field has no content
        return section.hideWhenEmpty.dataFields.every(fieldName => {
            const value: any = data[fieldName];

            // Check arrays on non deleted entries
            if (Array.isArray(value)) return !value.some((entry: any) => entry && entry.deleted !== true);

            // Check strings on text content without html tags
            if (typeof value === 'string') return value.replace(/<[^>]*>/g, '').trim() === '';

            // Treat other values as empty when null or undefined
            return value == null;
        });
    };

    // Generates the card components
    const generateCardComponent = (
        viewKey: string,
        editable: EditableStatusType,
        componentType: string, 
        fields: FieldType[] | undefined, 
        fieldsColumns: { title?: string; fields: FieldType[] }[] | undefined, 
        tabs: TabType[] | undefined,
        title: string | undefined,
        itemId: string, 
        apiObject: string, 
        data: any, 
        apiField: string | undefined, 
        alignment: 'horizontal' | 'stacked' | undefined,
        dataCondition?: { [key: string]: any },
    ) => {
        // If a data condition is set for the card, only render it if the corresponding data object has this value
        if (dataCondition && data) {
            const shouldDisplayCard = dataCondition.every((condition: { [key: string]: any }) => {
                // Evaluate a field based condition which requires a non empty value or list
                if (condition.field && condition.notEmpty) {
                    const fieldValue = data[condition.field];
                    return Array.isArray(fieldValue) ? fieldValue.length > 0 : Boolean(fieldValue);
                }

                // Evaluate a plain key value condition
                return Object.entries(condition).every(([key, value]) => data[key] === value)
            });
            if (!shouldDisplayCard) {
                return null;
            }
        }

        switch (componentType) {
            case 'FieldsCard':
                return <FieldsCard apiObject={apiObject} viewKey={viewKey} isEditable={editable} itemId={itemId} objectType={objectType} title={title} fields={fields} fieldColumns={fieldsColumns} data={data} dropdownData={dropdownData} groupOptions={groupOptions} alignment={alignment} loadStatus={loadStatus} refetchData={fetchData} />
            case 'PhotosCard':
                return <PhotosCard apiObject={apiObject} viewKey={viewKey} isEditable={editable} itemId={itemId} title={title} data={data} dropdownData={dropdownData} groupOptions={groupOptions} alignment={alignment} loadStatus={loadStatus} refetchData={fetchData} />
            case 'TabsCard':
                return <TabsCard apiObject={apiObject} tabs={tabs} viewKey={viewKey} itemId={itemId} title={title} data={data} dropdownData={dropdownData} groupOptions={groupOptions} alignment={alignment} loadStatus={loadStatus} refetchData={fetchData} />
            case 'SignaturesSection':
                return <SignatureSection data={data?.signatures ?? []} loadStatus={loadStatus} />
            case 'InvoiceScheduleCard':
                return <InvoiceScheduleCard data={data} />
            case 'MessagesCard':
                return <MessagesCard apiObject={apiObject} viewKey={viewKey} itemId={itemId} title={title} data={data} dropdownData={dropdownData} groupOptions={groupOptions} alignment={alignment} loadStatus={loadStatus} refetchData={fetchData} />
            // case 'PeopleCard':
            //     return <PeopleCard apiObject={apiObject} viewKey={viewKey} isEditable={editable} itemId={itemId} personIds={apiField ? data[apiField] : undefined} loadStatus={loadStatus} />
            // Plug in other card components
            default:
                return null;
        }
    }

    // Render the header buttons
    const renderHeaderButtons = (buttonConfig: HeaderButtonsConfig) => {

        // Return if the props are not given
        if (!apiObject || !itemId || !data || !headerButtons) return;

        // If the object is deleted, render only a restore button
        if (data['deleted']) {
            const deletedButton: HeaderButtons = [
                {
                    type: 'secondary',
                    label: 'button.restore_button_label',
                    action: 'restore',
                }
            ];

            return (
                <div className="button-container">
                    {generateHeaderButtons(
                        deletedButton,
                        apiObject,
                        data,
                        dropdownData,
                        itemId,
                        initializeModal,
                        buttonLoader,
                        setButtonLoader,
                        setFloatingAlert,
                        handleLogout,
                        { activeFeatures, roleFeatures, allowedFeatures, allowedRights },
                        userTimezone,
                        userLocale,
                        t,
                        closeModal,
                        fetchData,
                        refetchList,
                        undefined,
                        userHash
                    )}
                </div>
            );
        }

        // Otherwise render normal header buttons
        const generateButtons = (buttons: HeaderButtons) =>
            generateHeaderButtons(
                buttons,
                apiObject,
                data,
                dropdownData,
                itemId,
                initializeModal,
                buttonLoader,
                setButtonLoader,
                setFloatingAlert,
                handleLogout,
                { activeFeatures, roleFeatures, allowedFeatures, allowedRights },
                userTimezone,
                userLocale,
                t,
                closeModal,
                fetchData,
                refetchList,
                undefined,
                userHash
            );
    
        // If the header button config is an array, use the default button rendering
        if (Array.isArray(buttonConfig)) {
            return (
                <div className="button-container">
                    {generateButtons(buttonConfig)}
                </div>
            );
        }
    
        // Render if the header button config is an object with left and/or right buttons
        const { leftButtons = [], rightButtons = [] } = buttonConfig;
    
        return (
            <div className="button-container full-width-buttons">
                <div className="left-buttons">{generateButtons(leftButtons)}</div>
                <div className="right-buttons">{generateButtons(rightButtons)}</div>
            </div>
        );
    };

    return (
        <div className={`detailmodal row ${data?.['deleted'] ? 'deleted' : ''} ${summary ? 'summary' : ''} `}>
            <div style={{ width: '100%' }}>
                <div className='header'>
                    <div className='title-bar'>
                        <GenerateHeaderTitle apiObject={apiObject} data={data} />
                        {headerButtons && Array.isArray(headerButtons) ? (
                            // Render header buttons config consisting of a single array
                            renderHeaderButtons(headerButtons)
                        ) : null}
                    </div>
                    {headerButtons && !Array.isArray(headerButtons) && (
                        // Render header buttons config consisting of left and right aligned buttons
                        renderHeaderButtons(headerButtons)
                    )}
                    {statusBar && statuses && objectType && (
                        // Render the status bar of the detail modal
                        <StatusBar 
                            statuses={statuses} 
                            currentStatus={currentStatus} 
                        />
                    )}
                </div>
                <div className='content'>
                    {sections &&
                        // Render the sections of the detail modal
                        <div>
                            {sections.map((section, index) =>
                                <div key={index}>
                                    {section.component && itemId && apiObject && viewKey && !shouldHideEmptyCard(section) &&
                                        generateCardComponent(
                                            viewKey,
                                            editable,
                                            section.component,
                                            section.fields || [],
                                            section.fieldColumns || [],
                                            section.tabs,
                                            section.title,
                                            itemId.toString(),
                                            apiObject,
                                            data,
                                            section.apiField,
                                            section.alignment,
                                            isArrayOfConditions(section.dataCondition) ? section.dataCondition : undefined
                                        )
                                    }
                                </div>
                            )}
                        </div>
                    }
                    {tabs &&
                        // Render the tabs with its child components of the detail modal
                        <div>
                            {tabLabel && tabLabel.length > 1 && (
                                <ul className='card tabbar'>
                                    {tabLabel}
                                </ul>
                            )}
                            {tabComponent}                       
                        </div>
                    }
                </div>
            </div>
            
            {summary && objectType && viewKey && itemId &&
                // Render the summary next to the detail modal
                <DetailModalSummary 
                    viewKey={viewKey}
                    objectType={objectType} 
                    itemId={itemId}
                    data={data}     
                />
            }
        </div>
    );
};

export default DetailModal;